/**
 * NotificationItem
 *
 * Render de una sola `AppNotification`. Usado dentro del `NotificationsPanel`.
 *
 *  - Avatar con icono por kind.
 *  - Título (bold si unread) + body + tiempo relativo.
 *  - Dot rojo a la derecha si `read === false`.
 *  - onClick: navega al `actionPath` (si existe) y notifica `onAction`.
 */

import { memo } from 'react';
import {
  ListItemButton,
  ListItemAvatar,
  Avatar,
  ListItemText,
  Typography,
  Box,
} from '@mui/material';
import {
  PersonAdd as PersonAddIcon,
  ChatBubble as ChatBubbleIcon,
  SystemUpdate as SystemUpdateIcon,
  ErrorOutline as ErrorOutlineIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import type { AppNotification, NotificationKind } from '@/store/notificationsStore';

interface KindStyle {
  bg: string;
  fg: string;
}

const KIND_STYLES: Record<NotificationKind, { icon: React.ReactElement; style: KindStyle }> = {
  enrollment: {
    icon: <PersonAddIcon fontSize="small" />,
    style: { bg: 'warning.main', fg: 'warning.contrastText' },
  },
  chat: {
    icon: <ChatBubbleIcon fontSize="small" />,
    style: { bg: 'primary.main', fg: 'primary.contrastText' },
  },
  update: {
    icon: <SystemUpdateIcon fontSize="small" />,
    style: { bg: 'info.main', fg: 'info.contrastText' },
  },
  'update-failed': {
    icon: <ErrorOutlineIcon fontSize="small" />,
    style: { bg: 'error.main', fg: 'error.contrastText' },
  },
};

function relativeTime(iso?: string): string {
  if (!iso) return '';
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return '';
  const diff = Date.now() - t;
  if (diff < 0) return 'ahora';
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'ahora';
  if (mins < 60) return `hace ${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `hace ${hrs}h`;
  const days = Math.floor(hrs / 24);
  return `hace ${days}d`;
}

interface NotificationItemProps {
  notification: AppNotification;
  onAction?: (notification: AppNotification) => void;
}

function NotificationItemImpl({ notification, onAction }: NotificationItemProps) {
  const navigate = useNavigate();
  const style = KIND_STYLES[notification.kind];

  const handleClick = () => {
    if (notification.actionPath) {
      navigate(notification.actionPath);
    }
    onAction?.(notification);
  };

  return (
    <ListItemButton
      onClick={handleClick}
      data-testid={`notification-item-${notification.id}`}
      sx={{
        alignItems: 'flex-start',
        py: 1.25,
        px: 2,
        opacity: notification.read ? 0.7 : 1,
      }}
    >
      <ListItemAvatar sx={{ minWidth: 44, mt: 0.25 }}>
        <Avatar
          sx={{
            bgcolor: style.style.bg,
            color: style.style.fg,
            width: 32,
            height: 32,
          }}
        >
          {style.icon}
        </Avatar>
      </ListItemAvatar>
      <ListItemText
        primary={
          <Typography
            variant="body2"
            sx={{
              fontWeight: notification.read ? 500 : 700,
              display: 'inline',
            }}
          >
            {notification.title}
          </Typography>
        }
        secondary={
          <Box component="span" sx={{ display: 'flex', flexDirection: 'column', mt: 0.25 }}>
            <Typography
              component="span"
              variant="body2"
              color="text.secondary"
              sx={{
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {notification.body}
            </Typography>
            <Typography component="span" variant="caption" color="text.disabled" sx={{ mt: 0.25 }}>
              {relativeTime(notification.createdAt)}
            </Typography>
          </Box>
        }
      />
      {!notification.read && (
        <Box
          aria-label="no leída"
          data-testid={`notification-unread-${notification.id}`}
          sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            bgcolor: 'error.main',
            mt: 1.25,
            ml: 1,
            flexShrink: 0,
          }}
        />
      )}
    </ListItemButton>
  );
}

export const NotificationItem = memo(NotificationItemImpl);
export default NotificationItem;
